import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { MetricsKey } from './metricsKeys';
import { Log } from 'pages/Logs/hooks';

export type MoveType = 'month' | 'week' | 'day' | 'year';
export type MetricsFlowType = '+' | '-' | 'both' | 'count';
export type MetricLog = Log & { count: number };

interface MetricsState {
  jointMove: string,
  moveType: MoveType,
  metricChecked: MetricsKey,
  metricsFlowType: MetricsFlowType,
  metricLogs: MetricLog[],
  chartsEnlarged: string,
  /** Up to three highest-parent scopes added to balance metrics (besides departments). */
  customBalanceScopeIds: string[],
}

const METRICS_CUSTOM_BALANCE_SCOPES_KEY = 'metrics_custom_balance_scopes';

const readCustomBalanceScopes = (): string[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(METRICS_CUSTOM_BALANCE_SCOPES_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw.map((id) => String(id || '').trim()).filter(Boolean).slice(0, 3);
  } catch {
    return [];
  }
};

const initialState: MetricsState = {
  jointMove: localStorage.getItem('joint_move') || '',
  moveType: localStorage.getItem('move_type') as MoveType || 'day',
  // This setting of All balances is temporary, check Navigation hooks
  metricChecked: 'All balances',
  metricsFlowType: 'both',
  metricLogs: [],
  chartsEnlarged: localStorage.getItem('metrics_charts_enlarged') || '',
  customBalanceScopeIds: readCustomBalanceScopes(),
};

export const counterSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    setJointMove: (state, action: PayloadAction<string>) => {
      state.jointMove = action.payload;
      localStorage.setItem('joint_move', action.payload);
    },
    setMoveType: (state, action: PayloadAction<MoveType>) => {
      state.moveType = action.payload;
      localStorage.setItem('move_type', action.payload);
    },
    setMetricChecked: (state, action: PayloadAction<MetricsKey>) => {
      state.metricChecked = action.payload;
    },
    setMetricsFlowType: (state, action: PayloadAction<MetricsFlowType>) => {
      state.metricsFlowType = action.payload;
    },
    setMetricLogs: (state, action: PayloadAction<MetricLog[]>) => {
      state.metricLogs = action.payload;
    },
    setChartsEnlarged: (state, action: PayloadAction<string>) => {
      state.chartsEnlarged = action.payload;
      localStorage.setItem('metrics_charts_enlarged', action.payload);
    },
    setCustomBalanceScopeIds: (state, action: PayloadAction<string[]>) => {
      state.customBalanceScopeIds = (action.payload || [])
        .map((id) => String(id || '').trim())
        .filter(Boolean)
        .slice(0, 3);
      localStorage.setItem(
        METRICS_CUSTOM_BALANCE_SCOPES_KEY,
        JSON.stringify(state.customBalanceScopeIds),
      );
    },
  },
});

export const {
  setJointMove,
  setMoveType,
  setMetricChecked,
  setMetricsFlowType,
  setMetricLogs,
  setChartsEnlarged,
  setCustomBalanceScopeIds,
} = counterSlice.actions;

export const selectJointMove = (state: RootState) => state.metrics.jointMove;
export const selectMoveType = (state: RootState) => state.metrics.moveType;
export const selectMetricChecked = (state: RootState) => {
  const checked = state.metrics.metricChecked;
  // Retired metric options — map leftover session selection to a current default.
  if ((checked as string) === 'Capacity') return 'All audits';
  // Legacy "Hospitality" metric label → All balances.
  if ((checked as string) === 'Hospitality') return 'All balances';
  if ((checked as string) === 'Inventory') return 'All balances';
  if ((checked as string) === 'Walk-ins') return 'All audits';
  if ((checked as string) === 'Users') return 'Employees';
  return checked;
};
export const selectMetricsFlowType = (state: RootState) => state.metrics.metricsFlowType;
export const selectMetricLogs = (state: RootState) => state.metrics.metricLogs;
export const selectChartsEnlarged = (state: RootState) => (
  state.metrics.chartsEnlarged === 'yes'
);
export const selectCustomBalanceScopeIds = (state: RootState) => (
  state.metrics.customBalanceScopeIds
);

export default counterSlice.reducer;
